import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import urltron from 'urltron';

import { globalAppStore } from 'hooks/useGlobalApp';

import { parsePath } from 'utils/routes';

/**
 * A snapshot of what a page is currently showing, contributed via `useChatPageData`.
 * `rows` should already be the on-screen slice — pages with huge datasets should
 * pass only what's visible; the collector additionally caps it as a safety valve.
 */
export interface PageDataSnapshot {
  /** Human label for the data, e.g. a table title. */
  title?: string;
  /** The rows currently visible on screen. */
  rows?: unknown[];
  /** Any extra structured context the page wants to expose. */
  [key: string]: unknown;
}

/** The page context sent alongside each question from the page-aware drawer. */
export interface PageContext {
  /** Matching route path, e.g. '/:tenant/sales/customer/:id'. */
  routePath: string;
  /** module/submodule slug pair, e.g. 'sales/customers'. */
  page: string;
  /** Path params, e.g. { id: '123' }. */
  pathParams?: Record<string, string>;
  tenantName?: string;
  /** Active company filter, if any. */
  companyIds?: string[];
  /** Parsed URL-hash filter/form state for the page. */
  filters?: Record<string, unknown>;
  /** Structured tables scraped from the screen (plus any `useChatPageData` opt-ins). */
  onScreen: PageDataSnapshot[];
  /** Best-effort visible text of the app shell — detail-page fields, side panel, etc. */
  pageText?: string;
}

/** Cap rows per snapshot so a large on-screen table can't blow the prompt budget. */
const MAX_ROWS_PER_SNAPSHOT = 50;

/** Serialize for the prompt, capping length so a large payload can't blow the budget. */
function safeJson(value: unknown, maxChars: number): string {
  let str: string;
  try {
    str = JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
  return str.length > maxChars ? `${str.slice(0, maxChars)}… (truncated)` : str;
}

/**
 * Render page context as a compact preamble folded into the outbound question, so
 * the agent can resolve references like "this customer". Returns '' when there's nothing useful,
 * in which case the question is sent as-is.
 */
export function formatPageContext(ctx: PageContext): string {
  const lines: string[] = [];
  if (ctx.page) {
    lines.push(`Page: ${ctx.page}${ctx.routePath ? ` (route ${ctx.routePath})` : ''}`);
  } else if (ctx.routePath) {
    lines.push(`Route: ${ctx.routePath}`);
  }
  if (ctx.pathParams && Object.keys(ctx.pathParams).length) {
    lines.push(
      `Path params: ${Object.entries(ctx.pathParams)
        .map(([key, value]) => `${key}=${value}`)
        .join(', ')}`,
    );
  }
  if (ctx.companyIds?.length) {
    lines.push(`Active company filter: ${ctx.companyIds.join(', ')}`);
  }
  if (ctx.filters && Object.keys(ctx.filters).length) {
    lines.push(`Page filters: ${safeJson(ctx.filters, 1000)}`);
  }
  if (ctx.onScreen.length) {
    lines.push(`Data tables on screen: ${safeJson(ctx.onScreen, 4000)}`);
  }
  if (ctx.pageText) {
    lines.push(`Other visible text (detail fields, side panel, headings): ${ctx.pageText}`);
  }
  if (!lines.length) {
    return '';
  }
  return `[Page context] The user is asking this while viewing a page in the app. Use it to interpret references, but verify against the database rather than relying solely on the on-screen values.
${lines.join('\n')}`;
}

type Registrant = () => PageDataSnapshot | null;

interface ChatRegistryValue {
  /** Register an on-screen-data provider; returns an unregister fn. */
  register: (fn: Registrant) => () => void;
  /** Snapshot the current page context (metadata + registered on-screen data). */
  collectPageContext: () => PageContext;
}

interface ChatDrawerValue {
  /** Whether the chat drawer is open (shared so the header button and drawer sync). */
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
}

// Two contexts on purpose: the registry value is stable for the app's lifetime, while
// the drawer value changes on every open/close. Keeping them separate means toggling
// the drawer doesn't re-run every page's `useChatPageData` effect or re-render the nav.
const ChatRegistryContext = createContext<ChatRegistryValue | null>(null);
const ChatDrawerContext = createContext<ChatDrawerValue | null>(null);

function boundSnapshot(snapshot: PageDataSnapshot): PageDataSnapshot {
  if (Array.isArray(snapshot.rows) && snapshot.rows.length > MAX_ROWS_PER_SNAPSHOT) {
    return { ...snapshot, rows: snapshot.rows.slice(0, MAX_ROWS_PER_SNAPSHOT) };
  }
  return snapshot;
}

/** Caps for scraped tables (on top of MAX_ROWS_PER_SNAPSHOT) so the prompt stays bounded. */
const MAX_SCRAPED_TABLES = 3;
const MAX_SCRAPED_COLS = 15;

function isVisible(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Best-effort scrape of the Ant Design data tables currently rendered on the page, so
 * the chat gets "what's on screen" with no per-page wiring. Reads *display* text
 * (already formatted), which is why `formatPageContext` tells the agent to verify
 * against the database. Tables inside the chat drawer itself are skipped, and output
 * is bounded (tables/rows/cols). Pages needing richer/structured context can still
 * opt in via `useChatPageData`; those snapshots are merged with these.
 */
function scrapeVisibleTables(): PageDataSnapshot[] {
  if (typeof document === 'undefined') return [];

  return (
    Array.from(document.querySelectorAll('.ant-table'))
      // Skip the chat drawer's own artifact tables, and anything not actually visible.
      .filter((table) => !table.closest('.ant-drawer') && isVisible(table))
      .slice(0, MAX_SCRAPED_TABLES)
      .map((table) => {
        // Last header row handles grouped headers; body excludes measure/placeholder rows.
        const headers = Array.from(table.querySelectorAll('.ant-table-thead tr:last-child th')).map((th) =>
          (th.textContent || '').trim(),
        );
        const bodyRows = Array.from(table.querySelectorAll('.ant-table-tbody tr.ant-table-row')).slice(
          0,
          MAX_ROWS_PER_SNAPSHOT,
        );
        if (!bodyRows.length) return null;

        const rows = bodyRows.map((tr) => {
          const row: Record<string, string> = {};
          Array.from(tr.querySelectorAll('td'))
            .slice(0, MAX_SCRAPED_COLS)
            .forEach((td, i) => {
              const text = (td.textContent || '').trim();
              if (text) row[headers[i] || `col${i + 1}`] = text; // skip empty (selection/expand) cells
            });
          return row;
        });
        return rows;
      })
      .filter((rows): rows is Record<string, string>[] => rows != null)
      .map((rows, i) => ({ title: `On-screen table ${i + 1}`, rows }))
  );
}

/** Char budget for the general visible-text capture. */
const MAX_PAGE_TEXT_CHARS = 5000;
// Skip tables (captured structurally by scrapeVisibleTables), portaled overlays, and non-content nodes.
const PAGE_TEXT_EXCLUDE = '.ant-table, .ant-drawer, .ant-modal, script, style, noscript';

/**
 * Best-effort capture of the visible text in the app shell — covers detail-page
 * fields, the side panel, headings, etc. that aren't tables. Scoped to `.AppContainer`
 * so Ant Design's portaled overlays (our own chat drawer, modals, Intercom) — which
 * mount at document.body — are naturally excluded. Walks real (laid-out) nodes so
 * hidden content is skipped, and is length-bounded.
 */
function scrapePageText(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const root = document.querySelector('.AppContainer') ?? document.body;
  if (!root) return undefined;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent || parent.closest(PAGE_TEXT_EXCLUDE)) return NodeFilter.FILTER_REJECT;
      // offsetParent is null for display:none / fixed subtrees — treat those as not visible.
      if (!parent.offsetParent) return NodeFilter.FILTER_REJECT;
      return (node.textContent || '').trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });

  const parts: string[] = [];
  let total = 0;
  let node = walker.nextNode();
  while (node && total < MAX_PAGE_TEXT_CHARS) {
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (text) {
      parts.push(text);
      total += text.length + 1;
    }
    node = walker.nextNode();
  }
  const joined = parts.join(' ').trim();
  return joined ? joined.slice(0, MAX_PAGE_TEXT_CHARS) : undefined;
}

function collect(registry: Map<number, Registrant>): PageContext {
  const { activeTenant, activeCompanyIds } = globalAppStore.state;
  const parsed = parsePath(window.location.pathname);

  let filters: Record<string, unknown> | undefined;
  try {
    const parsedHash = urltron.parse(window.location.hash) as Record<string, unknown>;
    if (parsedHash && Object.keys(parsedHash).length) filters = parsedHash;
  } catch {
    // malformed hash — omit filters rather than fail the whole context
  }

  // Explicit per-page opt-ins (useChatPageData) first, then the generic DOM scrape.
  const registered = Array.from(registry.values())
    .map((fn) => {
      try {
        return fn();
      } catch {
        return null;
      }
    })
    .filter((s): s is PageDataSnapshot => s != null);
  const onScreen = [...registered, ...scrapeVisibleTables()].map(boundSnapshot);

  return {
    routePath: parsed.routePath,
    page: [parsed.moduleSlug, parsed.subModuleSlug].filter(Boolean).join('/'),
    pathParams: parsed.pathParams,
    tenantName: activeTenant?.name,
    companyIds: activeCompanyIds ?? undefined,
    filters,
    onScreen,
    pageText: scrapePageText(),
  };
}

/**
 * Provides the page-context registry to the page-aware chat drawer. Mount once near
 * the app root so both pages (which register on-screen data) and the drawer (which
 * collects it) share one registry.
 */
export function ChatContextProvider({ children }: { children: React.ReactNode }) {
  const registryRef = useRef(new Map<number, Registrant>());
  const nextIdRef = useRef(0);

  const register = useCallback((fn: Registrant) => {
    const id = nextIdRef.current;
    nextIdRef.current += 1;
    registryRef.current.set(id, fn);
    return () => {
      registryRef.current.delete(id);
    };
  }, []);

  const collectPageContext = useCallback(() => collect(registryRef.current), []);

  // Stable for the provider's lifetime (deps never change) — consumers don't churn.
  const registryValue = useMemo<ChatRegistryValue>(
    () => ({ register, collectPageContext }),
    [register, collectPageContext],
  );

  const [isChatOpen, setIsChatOpen] = useState(false);
  const openChat = useCallback(() => setIsChatOpen(true), []);
  const closeChat = useCallback(() => setIsChatOpen(false), []);
  const drawerValue = useMemo<ChatDrawerValue>(
    () => ({ isChatOpen, openChat, closeChat }),
    [isChatOpen, openChat, closeChat],
  );

  return (
    <ChatRegistryContext.Provider value={registryValue}>
      <ChatDrawerContext.Provider value={drawerValue}>{children}</ChatDrawerContext.Provider>
    </ChatRegistryContext.Provider>
  );
}

/** Page-context registry — used by the drawer (to collect) and `useChatPageData`. */
export function useChatRegistry(): ChatRegistryValue | null {
  return useContext(ChatRegistryContext);
}

/** Drawer open-state — used by the header launcher and the drawer itself. */
export function useChatDrawer(): ChatDrawerValue | null {
  return useContext(ChatDrawerContext);
}

/**
 * Opt-in from any page/component to expose what's currently on screen (e.g. the
 * visible table rows) to the page-aware chat drawer. The provider re-reads the
 * latest `getSnapshot` at send time, so it always reflects current state, and it
 * auto-unregisters on unmount.
 */
export function useChatPageData(getSnapshot: () => PageDataSnapshot | null): void {
  const registry = useChatRegistry();
  const fnRef = useRef(getSnapshot);
  fnRef.current = getSnapshot;

  useEffect(() => {
    if (!registry) return undefined;
    return registry.register(() => fnRef.current());
  }, [registry]);
}
